export const baseDurations = ['b', 'w', 'h', 'q', '8', '16', '32', '64', '128'] as const;
export type BaseDuration = typeof baseDurations[number];

export const baseDurationNames: Record<BaseDuration, string> = {
  'b': 'breve',
  'w': 'whole',
  'h': 'half',
  'q': 'quarter',
  '8': 'eighth',
  '16': 'sixteenth',
  '32': 'thirty-second',
  '64': 'sixty-fourth',
  '128': 'one-hundred-twenty-eighth'
}

export function isValidBaseDuration(input: any): input is BaseDuration {
  if (typeof input !== 'string') return false;
  return baseDurations.includes(input as BaseDuration);
}

export function assertValidBaseDuration(input: string): asserts input is BaseDuration {
  if (!isValidBaseDuration(input)) throw TypeError(`NaD: "${input}" is not a valid BaseDuration`);
}

declare const validRhythmicDuration: unique symbol;

export const rhythmNamesDictionary: Record<BaseDuration, string> = {
  b: 'Breve',
  w: 'Whole',
  h: 'Half',
  q: 'Quarter',
  "8": 'Eighth',
  "16": 'Sixteenth',
  "32": 'Thirty-second',
  "64": 'Sixty-fourth',
  "128": 'Hundred-twenty-eighth',
} as const;

export type RhythmicDuration =
  | BaseDuration
  | (string & {
      [validRhythmicDuration]: true;
    });

export function assertValidRhythmicDuration(input: string): asserts input is RhythmicDuration {
  if (!isValidRhythmicDuration(input)) {
    throw new TypeError(`NaD: "${input}" is not a valid RhythmicDuration`);
  }
}

export function isValidRhythmicDuration(input: any): input is RhythmicDuration {
  // It must be a string with length
  if (typeof input !== 'string' || input.length === 0) return false;

  // One and only one of the base duration strings is present:
  const validBaseDurations = ['128', '64', '32', '16', '8', 'q', 'h', 'w', 'b'];
  let includes128 = false;
  const howManyBaseDurations = validBaseDurations.filter((duration) => {
    const isPresent = input.includes(duration);
    if (duration === '128' && isPresent) includes128 = true;
    if (duration === '8' && includes128) return false;
    return isPresent;
  }).length;
  if (howManyBaseDurations !== 1) return false;

  // No non-allowed characters are present:
  const validCharRegEx = /[bwhq816324t.]/g;
  if (input.replace(validCharRegEx, '').length !== 0) return false;

  // Dots are only present after the base duration:
  const dotIndex = input.indexOf('.');
  const hasDot = dotIndex !== -1;
  const dotAfterOtherCharacters =
    !hasDot || input.split('.').filter((s) => s)[0].length === dotIndex;
  if (!dotAfterOtherCharacters) return false;
  // Is there a tie in the wrong place
  const tieIndex = input.indexOf('t');
  if (tieIndex !== -1 && tieIndex !== input.length - 1) return false;
  return true;
}

export const rhythmicAliases = {
  '1': 'w',
  '2': 'h',
  '4': 'q',
  e: '8',
  s: '16',
} as const;

export const rhythmicFractions: Record<BaseDuration, string> = {
  b: '2/1',
  w: '1/1',
  h: '1/2',
  q: '1/4',
  '8': '1/8',
  '16': '1/16',
  '32': '1/32',
  '64': '1/64',
  '128': '1/128',
};

export const timeSignatures = [
  '1/4',
  '2/4',
  '3/4',
  '4/4',
  '5/4',
  '6/4',
  '7/4',
  '8/4',
  '9/4',
  '12/4',
  '15/4',
  '1/2',
  '2/2',
  '3/2',
  '4/2',
  '6/2',
  '9/2',
  '12/2',
  '15/2',
  '2/1',
  '3/1',
  '1/8',
  '2/8',
  '3/8',
  '4/8',
  '5/8',
  '6/8',
  '7/8',
  '8/8',
  '9/8',
  '12/8',
  '15/8',
  '1/16',
  '2/16',
  '3/16',
  '4/16',
  '5/16',
  '6/16',
  '9/16',
  '12/16',
  '15/16',
] as const;

export type TimeSignatureString = typeof timeSignatures[number];

export function isTimeSignatureString(tsString: any): tsString is TimeSignatureString {
  return timeSignatures.indexOf(tsString) !== -1;
}

export type TimeSignatureAlias = TimeSignatureString | 'C' | 'C|';

export const timeSignatureAliases: TimeSignatureAlias[] = [...timeSignatures, 'C', 'C|'];

export function isTimeSignatureAlias(tsAlias: any): tsAlias is TimeSignatureAlias {
  return isTimeSignatureString(tsAlias) || tsAlias === 'C' || tsAlias === 'C|';
}

export const meterBeatDuration: Record<TimeSignatureString, string | string[]> = {
  '1/4': 'q',
  '2/4': 'q',
  '3/4': 'q',
  '4/4': 'q',
  '5/4': 'q',
  '6/4': 'h.',
  '7/4': 'q',
  '8/4': 'q',
  '9/4': 'h.',
  '12/4': 'h.',
  '15/4': 'h.',
  '1/2': 'h',
  '2/2': 'h',
  '3/2': 'h',
  '4/2': 'h',
  '6/2': 'w.',
  '9/2': 'w.',
  '12/2': 'w.',
  '15/2': 'w.',
  '2/1': 'w',
  '3/1': 'w',
  '1/8': 'e',
  '2/8': 'e',
  '3/8': 'e',
  '4/8': 'e',
  '5/8': ['q.', 'q'],
  '6/8': 'q.',
  '7/8': ['q', 'q', 'q.'],
  '8/8': ['q.', 'q.', 'q'],
  '9/8': 'q.',
  '12/8': 'q.',
  '15/8': 'q.',
  '1/16': 's',
  '2/16': 's',
  '3/16': 's',
  '4/16': 's',
  '5/16': ['8.', '8'],
  '6/16': '8.',
  '9/16': '8.',
  '12/16': '8.',
  '15/16': '8.',
};

export const meterBeamDuration: Record<TimeSignatureString, string | string[]> = {
  ...meterBeatDuration,
  '3/8': 'q.',
  '3/16': 'e.',
  '4/8': 'q',
};

export const meterSubdivision: Record<TimeSignatureString, string> = {
  '1/4': 'e',
  '2/4': 'e',
  '3/4': 'e',
  '4/4': 'e',
  '5/4': 'e',
  '6/4': 'q',
  '7/4': 'q',
  '8/4': 'q',
  '9/4': 'q',
  '12/4': 'q',
  '15/4': 'q',
  '1/2': 'q',
  '2/2': 'q',
  '3/2': 'q',
  '4/2': 'q',
  '6/2': 'h',
  '9/2': 'h',
  '12/2': 'h',
  '15/2': 'h',
  '2/1': 'h',
  '3/1': 'h',
  '1/8': '16',
  '2/8': '16',
  '3/8': '16',
  '4/8': '16',
  '5/8': 'e',
  '6/8': 'e',
  '7/8': 'e',
  '8/8': 'e',
  '9/8': 'e',
  '12/8': 'e',
  '15/8': 'e',
  '1/16': '32',
  '2/16': '32',
  '3/16': '32',
  '4/16': '32',
  '5/16': '32',
  '6/16': '16',
  '9/16': '16',
  '12/16': '16',
  '15/16': '16',
};

export const meterSimpleCutTimeDictionary: Record<
  TimeSignatureString,
  { simple: boolean; cutTime: boolean }
> = {
  '1/4': { simple: true, cutTime: false },
  '2/4': { simple: true, cutTime: false },
  '3/4': { simple: true, cutTime: false },
  '4/4': { simple: true, cutTime: false },
  '5/4': { simple: true, cutTime: false },
  '6/4': { simple: false, cutTime: false },
  '7/4': { simple: true, cutTime: false },
  '8/4': { simple: true, cutTime: false },
  '9/4': { simple: false, cutTime: false },
  '12/4': { simple: false, cutTime: false },
  '15/4': { simple: false, cutTime: false },
  '1/2': { simple: true, cutTime: true },
  '2/2': { simple: true, cutTime: true },
  '3/2': { simple: true, cutTime: true },
  '4/2': { simple: true, cutTime: true },
  '6/2': { simple: false, cutTime: false },
  '9/2': { simple: false, cutTime: false },
  '12/2': { simple: false, cutTime: false },
  '15/2': { simple: false, cutTime: false },
  '2/1': { simple: true, cutTime: true },
  '3/1': { simple: true, cutTime: true },
  '1/8': { simple: true, cutTime: false },
  '2/8': { simple: true, cutTime: false },
  '3/8': { simple: true, cutTime: false },
  '4/8': { simple: true, cutTime: false },
  '5/8': { simple: false, cutTime: false },
  '6/8': { simple: false, cutTime: false },
  '7/8': { simple: false, cutTime: false },
  '8/8': { simple: false, cutTime: false },
  '9/8': { simple: false, cutTime: false },
  '12/8': { simple: false, cutTime: false },
  '15/8': { simple: false, cutTime: false },
  '1/16': { simple: true, cutTime: false },
  '2/16': { simple: true, cutTime: false },
  '3/16': { simple: true, cutTime: false },
  '4/16': { simple: true, cutTime: false },
  '5/16': { simple: false, cutTime: false },
  '6/16': { simple: false, cutTime: false },
  '9/16': { simple: false, cutTime: false },
  '12/16': { simple: false, cutTime: false },
  '15/16': { simple: false, cutTime: false },
};

// Rhythmic fractions:
// Allowed exceptions for rhythmic transcription:
// exceptions[meterString][durationString][startString] = [ lastRhythm ]
export const rhythmTranscriptionAllowedExceptions = {
  // In 2/4 time
  '2/4': {
    // a quarter note duration is allowed:
    '0:1:0': {
      // if it begins on the 1st subdivision of beat 0
      // when following an eighth note.
      '0:0:1': ['8'],
    },
    // Dotted quarter notes:
    '0:1:1': {
      '0:0:1': ['8'],
    },
  },

  '3/4': {
    // Quarter notes:
    '0:1:0': {
      '0:0:1': ['8'],
      '0:1:1': ['q'],
    },
    // Dotted quarter notes:
    '0:1:1': {
      '0:0:1': ['8'],
      // Not on 0:1:1, or it'd look like 6/8!!!
    },
  },

  '4/4': {
    // Quarter notes:
    '0:1:0': {
      '0:0:1': ['8'],
      '0:1:1': ['q'],
      '0:2:1': ['q', '8'],
    },
    // Dotted quarter notes:
    '0:1:1': {
      '0:0:1': ['8'],
      '0:2:1': ['8'],
    },
  },

  '2/2': {
    // half notes:
    '0:1:0': {
      '0:0:1': ['*'],
      '0:1:1': ['*'],
      '0:2:1': ['*'],
    },
    // Dotted half notes:
    '0:1:1': {
      '0:0:1': ['*'],
      '0:1:1': ['*'],
      '0:2:1': ['*'],
    },
  },

  '3/2': {
    // half notes:
    '0:1:0': {
      '0:0:1': ['*'],
      '0:1:1': ['*'],
      '0:2:1': ['*'],
    },
    // Dotted half notes:
    '0:1:1': {
      '0:0:1': ['*'],
      '0:1:1': ['*'],
      '0:2:1': ['*'],
    },
  },

  '4/2': {
    // half notes:
    '0:1:0': {
      '0:0:1': ['*'],
      '0:1:1': ['*'],
      '0:2:1': ['*'],
    },
    // Dotted half notes:
    '0:1:1': {
      '0:0:1': ['*'],
      '0:1:1': ['*'],
      '0:2:1': ['*'],
    },
  },
};
// Forbidden exceptions for rhythmic transcription:
// forbidden[meter][duration][startpointer, startpointer]
export const rhythmTranscriptionForbiddenExceptions = {
  '4/4': {
    // dotted q beginning on beat 2:
    '0:1:1': ['0:1:0'],
    // double dotted q beginning on beat 2:
    '0:1:1.5': ['0:1:0'],
    // dotted quarter rests on offbeats:
    '0:1:1r': ['0:0:1', '0:2:1'],
    // dotted half note rest beginning on beat 2:
    '0:3:0r': ['0:1:0'],
    // offbeat quarter rests:
    '0:1:0r': ['0:0:1', '0:1:1', '0:2:1'],
  },
  '3/4': {
    // offbeat quarter rests:
    '0:1:0r': ['0:0:1', '0:1:1'],
  },
  '2/4': {
    // dotted quarter rests on offbeats:
    '0:1:1r': ['0:0:1'],
    // quarter rest on offbeat:
    '0:1:0r': ['0:0:1'],
  },
  // Half notes across beats in 6/8, 9/8, 12/8
  '6/8': {
    // double dotted q beginning on beat 0:
    '0:1:0.5': ['0:0:0'],
    // half note beginning on beat 0:
    '0:1:1': ['0:0:0'],
  },
  '9/8': {
    // double dotted q beginning on beat 0:
    '0:1:0.5': ['0:0:0', '0:1:0'],
    // half note beginning on beat 0:
    '0:1:1': ['0:0:0', '0:1:0'],
  },
  '12/8': {
    // double dotted q beginning on beat 0:
    '0:1:0.5': ['0:0:0', '0:1:0', '0:2:0'],
    // half note beginning on beat 0:
    '0:1:1': ['0:0:0', '0:1:0', '0:2:0'],
  },
  '2/2': {
    // dotted half rests are generally never used in cut time, per Elaine Gould
    // Behind Bars p. 162 "In minim meters, the longest dotted rest is a dotted crotchet"
    '0:1:1r': ['*'],
    // Although a half note on offbeat of one is allowed, the same is not true for rests:
    '0:1:0r': ['0:0:1', '0:1:1', '0:2:1', '0:3:1'],
  },
  '3/2': {
    // dotted half rests are generally never used in cut time, per Elaine Gould
    // Behind Bars p. 162 "In minim meters, the longest dotted rest is a dotted crotchet"
    '0:1:1r': ['*'],
    // Although a half note on offbeat of one is allowed, the same is not true for rests:
    '0:1:0r': ['0:0:1', '0:1:1', '0:2:1', '0:3:1'],
  },
  '4/2': {
    // dotted half rests are generally never used in cut time, per Elaine Gould
    // Behind Bars p. 162 "In minim meters, the longest dotted rest is a dotted crotchet"
    '0:1:1r': ['*'],
    // Although a half note on offbeat of one is allowed, the same is not true for rests:
    '0:1:0r': ['0:0:1', '0:1:1', '0:2:1', '0:3:1'],
  },
};
